export const SIZE_TYPE = {
  LARGE: 'large',
  MEDIUM: 'medium',
  SMALL: 'small'
} as const;

export const SIZES = {
  [SIZE_TYPE.LARGE]: 1600,
  [SIZE_TYPE.MEDIUM]: 800,
  [SIZE_TYPE.SMALL]: 400
};

export function getFormattedVariants(variants) {
  return Object.fromEntries(
    variants.map(({ dimensions, type, ...props }) => [
      type,
      {
        ...props,
        ...dimensions
      }
    ])
  );
}

function calculateHeight(width, aspectRatio) {
  return Math.round(width * aspectRatio);
}

function createImageVariant(url, value, currentWidth, calculatedHeight) {
  return {
    url: url.replace(value, `w${currentWidth}-h${calculatedHeight}`),
    width: currentWidth,
    height: calculatedHeight
  };
}

function getImageVariants({ url, variants, isUploaded }) {
  if (isUploaded) {
    if (variants.length) {
      return getFormattedVariants(variants);
    }

    return {
      [SIZE_TYPE.SMALL]: {
        url
      }
    };
  }

  const regex = /w(\d+)-h(\d+)/;
  const match = url.match(regex);

  if (!match) {
    return url;
  }

  const [value, width, height] = match;
  const parsedWidth = parseInt(width, 10);
  const parsedHeight = parseInt(height, 10);
  const aspectRatio = parsedHeight / parsedWidth;

  const getVariants = (sizes) => {
    return Object.fromEntries(
      Object.entries(sizes).map(([type, currentWidth]) => {
        const calculatedHeight = calculateHeight(currentWidth, aspectRatio);

        return [
          type,
          createImageVariant(url, value, currentWidth, calculatedHeight)
        ];
      })
    );
  };

  if (parsedWidth >= SIZES[SIZE_TYPE.LARGE]) {
    return getVariants(SIZES);
  }

  if (parsedWidth >= SIZES[SIZE_TYPE.MEDIUM]) {
    const { [SIZE_TYPE.LARGE]: _, ...otherSizes } = SIZES;

    return getVariants(otherSizes);
  }

  return {
    [SIZE_TYPE.SMALL]: createImageVariant(
      url,
      value,
      parsedWidth,
      parsedHeight,
      SIZE_TYPE.SMALL
    )
  };
}

export function getFormattedGoogleImages(images) {
  if (!Array.isArray(images)) {
    return {};
  }

  return images.map(({ pid, url, variants, isUploaded }) => {
    const image = getImageVariants({
      url,
      variants,
      isUploaded
    });
    const isEmpty = Object.values(image || {}).some(({ url }) => !url);

    return {
      image,
      isEmpty,
      title: '',
      description: '',
      link: '',
      id: pid,
      alt: '',
      categories: []
    };
  });
}
